import { useCallback, useEffect, useRef, useState } from 'react'
import {
  computeScoreboardAtTime,
  ScoreboardSyncResult,
  ScoreboardEvent,
} from '@sportninja/common/utils/games/scoreboardSync'
import type {
  WsTimelineState,
  WsGameState,
} from '@sportninja/common/utils/wsTimelineTransformer'

interface Livestream {
  stream_url?: string
  type?: string
  stream_started_at?: string
}

interface Game {
  startedAt?: string
  started_at?: string
  livestreams?: Livestream[]
  homeTeamId?: string
  visitingTeamId?: string
}

interface InitialState {
  homeScore?: number | null
  visitorScore?: number | null
  homeShots?: number | null
  visitorShots?: number | null
  displayTimer?: string | null
  displayPeriod?: string | null
}

interface SyncedState {
  homeScore: number | null
  visitorScore: number | null
  homeShots: number | null
  visitorShots: number | null
  displayTimer: string | null
  displayPeriod: string | null
  isPastLastEvent: boolean
}

interface UseVideoSyncedScoreParams {
  videoRef: { current: HTMLVideoElement | null }
  videoReady?: boolean
  game: Game | null | undefined
  videoUrl?: string | null
  selectedStreamType?: string
  isLive?: boolean
  scoreboardEvents: ScoreboardEvent[] | null | undefined
  initialState?: InitialState | null
  wsTimelineEvents?: WsTimelineState | null
  wsGameState?: WsGameState | null
  refetchScoreboard?: () => void
}

const useVideoSyncedScore = ({
  videoRef,
  videoReady,
  game,
  videoUrl,
  selectedStreamType,
  isLive = false,
  scoreboardEvents,
  initialState,
  wsTimelineEvents,
  wsGameState,
  refetchScoreboard,
}: UseVideoSyncedScoreParams): SyncedState => {
  const [syncedState, setSyncedState] = useState<SyncedState>(() => ({
    homeScore: initialState?.homeScore ?? null,
    visitorScore: initialState?.visitorScore ?? null,
    homeShots: initialState?.homeShots ?? null,
    visitorShots: initialState?.visitorShots ?? null,
    displayTimer: initialState?.displayTimer ?? (isLive ? '0:00' : null),
    displayPeriod: initialState?.displayPeriod ?? null,
    isPastLastEvent: false,
  }))

  // Cache previous values to avoid unnecessary re-renders
  const prevValuesRef = useRef<ScoreboardSyncResult | null>(null)

  const livestream = videoUrl
    ? game?.livestreams?.find((ls) => ls.stream_url === videoUrl)
    : game?.livestreams?.find((ls) => ls.type === selectedStreamType)

  const streamStartedAt = livestream?.stream_started_at

  // Refetch scoreboard when WS events arrive
  useEffect(() => {
    if (!wsTimelineEvents) {
      return
    }
    const goalCount = wsTimelineEvents.goals
      ? Object.keys(wsTimelineEvents.goals).length
      : 0
    const shotCount = wsTimelineEvents.shots
      ? Object.keys(wsTimelineEvents.shots).length
      : 0
    const penaltyCount = wsTimelineEvents.penalties
      ? Object.keys(wsTimelineEvents.penalties).length
      : 0
    const goalieChangeCount = wsTimelineEvents.goalieChanges
      ? Object.keys(wsTimelineEvents.goalieChanges).length
      : 0
    const deletedCount = wsTimelineEvents.deletedIds?.size || 0
    const hasEvents =
      goalCount > 0 ||
      shotCount > 0 ||
      penaltyCount > 0 ||
      goalieChangeCount > 0 ||
      deletedCount > 0

    if (hasEvents && typeof refetchScoreboard === 'function') {
      refetchScoreboard()
    }
  }, [wsTimelineEvents, refetchScoreboard])

  // Refetch scoreboard when period/shootout WS events arrive
  useEffect(() => {
    if (!wsGameState?.lastEvent) {
      return
    }
    const periodEvents = [
      'period.started',
      'period.ended',
      'shootout.started',
      'shootout.ended',
      'score.updated',
      'game.finalized',
    ]
    if (
      periodEvents.includes(wsGameState.lastEvent) &&
      typeof refetchScoreboard === 'function'
    ) {
      refetchScoreboard()
    }
  }, [wsGameState?.lastEvent, wsGameState?.lastEventAt, refetchScoreboard])

  const computeScores = useCallback(() => {
    const video = videoRef?.current
    if (!video) {
      return null
    }
    return computeScoreboardAtTime({
      currentTimeSec: video.currentTime,
      streamStartedAt,
      isLive,
      scoreboardEvents,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, videoReady, streamStartedAt, isLive, scoreboardEvents])

  useEffect(() => {
    const video = videoRef?.current
    if (!video || !scoreboardEvents || (!isLive && !streamStartedAt)) {
      setSyncedState((prev) =>
        prev.displayTimer !== null
          ? {
              homeScore: null,
              visitorScore: null,
              homeShots: null,
              visitorShots: null,
              displayTimer: null,
              displayPeriod: null,
              isPastLastEvent: false,
            }
          : prev
      )
      return
    }

    const handleTimeUpdate = () => {
      const result = computeScores()
      if (!result) {
        return
      }

      const prev = prevValuesRef.current
      // Only update state when values actually change
      if (
        !prev ||
        prev.homeScore !== result.homeScore ||
        prev.visitorScore !== result.visitorScore ||
        prev.homeShots !== result.homeShots ||
        prev.visitorShots !== result.visitorShots ||
        prev.displayTimer !== result.displayTimer ||
        prev.displayPeriod !== result.displayPeriod ||
        prev.isPastLastEvent !== result.isPastLastEvent
      ) {
        prevValuesRef.current = result
        setSyncedState(result)
      }
    }

    // Compute initial state
    handleTimeUpdate()

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [
    videoRef,
    videoReady,
    streamStartedAt,
    scoreboardEvents,
    isLive,
    computeScores,
  ])

  return syncedState
}

export default useVideoSyncedScore
